import { CSSProperties, useEffect, useState } from 'react';
import { formatDistanceStrict, formatRelative, isSameWeek } from 'date-fns';
import { dateFnsLocales } from 'shared-locales';

import type { ReduxRootState } from 'types';

interface Props {
  language: ReduxRootState['language'];
  relativeMode?: boolean;
  style?: CSSProperties;
  timeString: string;
}

const TimeIndicator = ({
  language,
  relativeMode,
  style,
  timeString
}: Props) => {
  const [fromNow, setFromNow] = useState('');

  useEffect(() => {
    setFromNow(getFormattedTime(timeString));
  }, []);

  let refreshTimeout: NodeJS.Timeout;
  useEffect(() => {
    refreshFromNow();

    return () => clearTimeout(refreshTimeout);
  }, [language, timeString]);

  const refreshFromNow = () => {
    setFromNow(getFormattedTime(timeString));
    refreshTimeout = setTimeout(refreshFromNow, 10000); // 10 seconds
  };

  const polishDayWithAdjective = (
    token,
    date: Date,
    baseDate: Date,
    options
  ): string => {
    const adjectivesLastWeek = {
      masculine: 'ostatni',
      feminine: 'ostatnia'
    };

    const adjectivesThisWeek = {
      masculine: 'ten',
      feminine: 'ta'
    };

    const adjectivesNextWeek = {
      masculine: 'następny',
      feminine: 'następna'
    };

    const dayGrammaticalGender = {
      0: 'feminine',
      1: 'masculine',
      2: 'masculine',
      3: 'feminine',
      4: 'masculine',
      5: 'masculine',
      6: 'feminine'
    };

    let adjectives;
    if (isSameWeek(date, baseDate, options)) {
      adjectives = adjectivesThisWeek;
    } else if (token === 'lastWeek') {
      adjectives = adjectivesLastWeek;
    } else if (token === 'nextWeek') {
      adjectives = adjectivesNextWeek;
    } else {
      throw new Error(`Cannot determine adjectives for token ${token}`);
    }

    const day = date.getDay() as Day;
    const grammaticalGender = dayGrammaticalGender[day];

    const adjective = adjectives[grammaticalGender];

    return `'${adjective}' eeee`;
  };

  const getFormattedTime = (timeString: string) => {
    if (relativeMode) {
      const relativeLocales = {
        'de-DE': {
          lastWeek: "'letzten' eeee",
          yesterday: "'gestern'",
          today: "'heute'",
          tomorrow: "'morgen'",
          nextWeek: 'eeee',
          other: 'P'
        },
        'en-GB': {
          lastWeek: "'last' eeee",
          yesterday: "'yesterday'",
          today: "'today'",
          tomorrow: "'tomorrow'",
          nextWeek: 'eeee',
          other: 'P'
        },
        'es-ES': {
          lastWeek: "'el' eeee 'pasado'",
          yesterday: "'ayer'",
          today: "'hoy'",
          tomorrow: "'mañana'",
          nextWeek: 'eeee',
          other: 'P'
        },
        'et-EE': {
          lastWeek: "'eelmine' eeee",
          yesterday: "'eile'",
          today: "'täna'",
          tomorrow: "'homme'",
          nextWeek: 'eeee',
          other: 'P'
        },
        'fi-FI': {
          lastWeek: "'viime' eeee",
          yesterday: "'eilen'",
          today: "'tänään'",
          tomorrow: "'huomenna'",
          nextWeek: 'eeee',
          other: 'P'
        },
        'fr-FR': {
          lastWeek: "eeee 'dernier'",
          yesterday: "'hier'",
          today: "'aujourd’hui'",
          tomorrow: "'demain'",
          nextWeek: "eeee 'prochain'",
          other: 'P'
        },
        'nb-NO': {
          lastWeek: "'forrige' eeee",
          yesterday: "'i går'",
          today: "'i dag'",
          tomorrow: "'i morgen'",
          nextWeek: 'eeee',
          other: 'P'
        },
        'pl-PL': {
          lastWeek: polishDayWithAdjective,
          yesterday: "'wczoraj'",
          today: "'dzisiaj'",
          tomorrow: "'jutro'",
          nextWeek: polishDayWithAdjective,
          other: 'P'
        },
        'ru-RU': {
          lastWeek: "'прошлая' eeee",
          yesterday: "'вчера'",
          today: "'cегодня'",
          tomorrow: "'завтра'",
          nextWeek: 'eeee',
          other: 'P'
        },
        'sv-SE': {
          lastWeek: "'I' eeee's'",
          yesterday: "'igår'",
          today: "'idag'",
          tomorrow: "'imorgon'",
          nextWeek: "'på' eeee",
          other: 'P'
        }
      };

      return formatRelative(new Date(timeString), new Date(), {
        locale: {
          ...dateFnsLocales[language],
          formatRelative: (token) => relativeLocales[language][token]
        },
        weekStartsOn: 1
      });
    } else {
      return formatDistanceStrict(new Date(timeString), new Date(), {
        addSuffix: true,
        locale: dateFnsLocales[language]
      });
    }
  };

  return (
    <span style={style}>
      {fromNow.charAt(0).toUpperCase() + fromNow.slice(1)}
    </span>
  );
};

export default TimeIndicator;
